import React, { useState, useEffect, useCallback } from 'react';
import { screens } from './screens';
import { config } from './config';

const PauseIcon: React.FC = () => (
  <div style={styles.pauseContainer}>
    <div style={styles.pauseIcon}>
      <div style={styles.pauseBar}></div>
      <div style={styles.pauseBar}></div>
    </div>
  </div>
);

const ScreenManager: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % screens.length);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + screens.length) % screens.length);
  }, []);

  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        goToNext();
      } else if (event.key === 'ArrowLeft') {
        goToPrevious();
      } else if (event.key === ' ') {
        event.preventDefault();
        togglePause();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious, togglePause]);

  useEffect(() => {
    // Only rotate on desktop
    if (isPaused || isMobile) return;

    const interval = setInterval(() => {
      goToNext();
    }, config.autoRotationInterval);

    return () => clearInterval(interval);
  }, [isPaused, isMobile, goToNext]);

  // Mobile: render all screens stacked vertically
  if (isMobile) {
    return (
      <div style={styles.mobileContainer}>
        {screens.map((screen) => {
          const ScreenComponent = screen.component;
          return (
            <div
              key={screen.id}
              style={styles.mobileScreenWrapper}
            >
              <ScreenComponent />
            </div>
          );
        })}
      </div>
    );
  }

  // Desktop: render screens with rotation
  return (
    <div style={styles.container}>
      {screens.map((screen, index) => {
        const ScreenComponent = screen.component;
        return (
          <div
            key={screen.id}
            style={{
              ...styles.screenWrapper,
              display: index === currentIndex ? 'block' : 'none'
            }}
          >
            <ScreenComponent />
          </div>
        );
      })}
      {isPaused && <PauseIcon />}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'relative',
    width: '100%',
    height: '100vh',
    overflow: 'hidden'
  },
  screenWrapper: {
    width: '100%',
    height: '100vh'
  },
  mobileContainer: {
    width: '100%',
    minHeight: '100vh',
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  mobileScreenWrapper: {
    width: '100%',
    minHeight: '100vh'
  },
  pauseContainer: {
    position: 'fixed',
    bottom: '20px',
    left: '20px',
    backgroundColor: 'rgba(134, 0, 56, 0.9)',
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    zIndex: 9999
  },
  pauseIcon: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center'
  },
  pauseBar: {
    width: '6px',
    height: '24px',
    backgroundColor: '#fdbb30',
    borderRadius: '2px'
  }
};

export default ScreenManager;
